import { colors } from '../../../config/styles';
import styled from 'styled-components';
import React, { CSSProperties, forwardRef, useCallback } from 'react';
import { Editor } from '@tiptap/react';
import { DangerButton, PrimaryButton, SecondaryButton } from '../../Button';
import { InputField } from '../../Form';
import { moveToEndOfMarkRange } from '../extentions/moveToEndOfMarkRange';
import { SvgIcon } from '../../Icon';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { isNil } from 'lodash';
import { zodResolver } from '@hookform/resolvers/zod';
import { zodFormErrors } from '../../../utils/zod';
import { theme } from '../../../styles/theme';
import { floatingComponentStyles } from '../../../styles/shareableStyles';
import KubitTooltip from '../../KubitTooltip';

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  width: 240px;
  padding: 12px;
  ${floatingComponentStyles};
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ActionRow = styled(Row)`
  justify-content: flex-end;
`;

const OpenLink = styled.a`
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  outline: none;
  color: ${theme.colors.secondary600};
  &:hover {
    cursor: pointer;
    color: ${theme.colors.mainBlue};
  }
`;

const Error = styled.div`
  color: ${colors.mainError};
`;

export enum LinkMenuMode {
  Add,
  Edit,
}

interface IProps {
  style?: CSSProperties;
  editor: Editor;
  mode: LinkMenuMode;
  onClose?: () => void;
}

const zLink = z
  .string()
  .min(1, 'Please enter a link')
  // If you need to allow more protocols, there must be a change in Kobol
  // to whitelist them
  .regex(/^https:\/\//, 'Only HTTPS links are allowed')
  .regex(/^https:\/\/.+/, 'The link is invalid');

const formSchema = z.object({
  link: zLink,
});

type IFormSchema = z.infer<typeof formSchema>;

export const LinkMenu = forwardRef<HTMLDivElement, IProps>(
  ({ mode, onClose, editor, style }, ref) => {
    const href = editor.getAttributes('link').href;

    const {
      handleSubmit,
      watch,
      formState: { errors },
      control,
    } = useForm<IFormSchema>({
      values: {
        link: href ?? '',
      },
      resolver: zodResolver(formSchema),
    });

    const removeLink = () => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .unsetLink()
        .setTextSelection(editor.state.selection.to)
        .run();
      onClose?.();
    };

    const updateHref = handleSubmit((form) => {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: form.link })
        .command(moveToEndOfMarkRange('link')) // this will close the menu
        .run();
      onClose?.();
    });

    const handleLinkMouseDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          updateHref();
        }
      },
      [updateHref],
    );

    const currentLink = watch('link');
    const currentLinkValidation = zLink.safeParse(currentLink);

    return (
      <Panel ref={ref} style={style}>
        <Group>
          <Row>
            <Controller
              control={control}
              name="link"
              render={({ field, fieldState: { error } }) => (
                <InputField
                  {...field}
                  inputMode="url"
                  autoFocus={mode === LinkMenuMode.Add}
                  hasError={!isNil(error)}
                  placeholder="E.g. https://kubit.ai"
                  onKeyDown={handleLinkMouseDown}
                />
              )}
            />
            <KubitTooltip
              disabled={!currentLinkValidation.success}
              content={
                currentLinkValidation.success ? (
                  <>Visit {currentLink}</>
                ) : (
                  zodFormErrors(currentLinkValidation.error)
                )
              }
              placement="top"
            >
              <OpenLink
                href={currentLinkValidation.success ? currentLink : undefined}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SvgIcon
                  name="arrow-square-up-right"
                  width={16}
                  height={16}
                  isDisabled={!currentLinkValidation.success}
                  customFill="currentColor"
                />
              </OpenLink>
            </KubitTooltip>
          </Row>

          {errors.link && <Error>{errors.link.message}</Error>}
        </Group>

        <ActionRow>
          {mode === LinkMenuMode.Add && (
            <>
              <SecondaryButton
                size="small"
                onClick={onClose}
                trackContext="link"
                trackAction="cancel"
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                size="small"
                onClick={updateHref}
                trackContext="link"
                trackAction="create"
              >
                Create
              </PrimaryButton>
            </>
          )}
          {mode === LinkMenuMode.Edit && (
            <>
              <DangerButton
                size="small"
                onClick={removeLink}
                trackContext="link"
                trackAction="remove"
              >
                Remove
              </DangerButton>
              <PrimaryButton
                size="small"
                onClick={updateHref}
                trackContext="link"
                trackAction="update"
              >
                Update
              </PrimaryButton>
            </>
          )}
        </ActionRow>
      </Panel>
    );
  },
);
